<template>
  <div ref="stickyWrapper" class="sticky-wrapper">
    <div
      ref="sticky"
      :class="{ 'is-stickable': stickable, 'is-fixed': fixed, 'is-absolute': absolute }"
      :style="{ bottom: bottomPos, top: topPos, width: width }"
    >
      <slot></slot>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';

export default {
  name: 'stickyelement',
  props: {
    useHeaderOffset: { type: Boolean, default: true },
    triggerOffset: { type: Number, default: 0 },
  },
  data() {
    return {
      stickable: true,
      fixed: false,
      absolute: false,
      initialOffset: 0,
      prevScroll: 0,
      headerOffset: 0,
      prevOffset: 0,
      screenHeight: 0,
      topPos: 'auto',
      bottomPos: 'auto',
      width: 'auto',
      lastKnownScrollPos: 0,
      ticking: false,
      isScrollingDown: false,
      stickySize: {
        width: 0,
        height: 0,
        top: 0,
      },
      smallScreen: window.ScreenSizeHandler
        ? window.ScreenSizeHandler.getMqForSize('small').selected
        : true,
    };
  },
  mounted() {
    this.$nextTick(() => {
      setTimeout(() => {
        this.initialOffset = this.getSize(this.$refs.sticky).top;
        this.calcSizes();
        this.setUpScrollHandler();
        this.handleScroll();
      }, 500);
    });
  },
  methods: {
    setUpScrollHandler() {
      /* istanbul ignore next */
      window.addEventListener('scroll', () => {
        this.lastKnownScrollPos = window.pageYOffset;
        if (!this.ticking) {
          window.requestAnimationFrame(() => {
            this.handleScroll(this.lastKnownScrollPos);
            this.ticking = false;
          });
        }
        this.ticking = true;
      });

      window.addEventListener('resize', debounce(this.calcSizes, 50));

      if (window.eventBus) {
        window.eventBus.$on('filter::resize', debounce(this.calcSizes, 50));
        window.eventBus.$on('headerHeight.updated', debounce(this.calcSizes, 50));
      }
    },
    handleScroll() {
      const scrollTop = this.lastKnownScrollPos;
      this.isScrollingDown = scrollTop >= this.prevScroll;

      /**
       * Case 1
       * scrolling down
       * __sticky is smaller than the screenheight__
       * the scroll is higher than the offset of the sticky
       */
      if (
        this.isScrollingDown &&
        this.stickySize.height + this.initialOffset < this.screenHeight &&
        scrollTop + this.headerOffset > this.initialOffset + this.triggerOffset
      ) {
        this.fixed = true;
        this.absolute = false;
        this.topPos = `${this.headerOffset}px`;
        this.bottomPos = 'auto';

        if (window.eventBus) {
          window.eventBus.$emit('stickyelement.stuck', this.$refs.sticky);
        }
      }

      /**
       * Case 2
       * scrolling up
       * __sticky is smaller than the screenheight__
       * the scroll is lower than the offset of the sticky
       */

      if (
        !this.isScrollingDown &&
        this.stickySize.height + this.initialOffset < this.screenHeight &&
        scrollTop + this.headerOffset < this.initialOffset
      ) {
        this.fixed = false;
        this.absolute = false;
        this.topPos = 'auto';
        this.bottomPos = 'auto';

        if (window.eventBus) {
          window.eventBus.$emit('stickyelement.unstuck', this.$refs.sticky);
        }
      }

      this.prevScroll = scrollTop;
      this.prevOffset = this.stickySize.top;
    },

    calcSizes() {
      /* istanbul ignore next */
      const headerNode = document.querySelector('.header-wrapper');

      if (this.useHeaderOffset && headerNode) {
        this.headerOffset = this.getSize(headerNode).height;
      } else {
        this.headerOffset = 0;
      }

      this.stickySize = this.getSize(this.$refs.sticky);
      this.screenHeight = window.innerHeight;
      this.width = `${this.getSize(this.$refs.stickyWrapper).width}px`;

      this.lastKnownScrollPos = window.pageYOffset;
      this.handleScroll();
    },
    getSize(node) {
      const bounding = node && node.getBoundingClientRect();
      return {
        height: bounding.bottom - bounding.top,
        width: bounding.right - bounding.left,
        top: bounding.top + window.pageYOffset,
      };
    },
  },
  destroy() {
    /* istanbul ignore next */
    window.removeEventListener('resize', this.calcSizes);
    window.removeEventListener('scroll', this.handleScroll);
  },
};
</script>

<style lang="scss">
.is-stickable {
  left: auto;
  right: auto;
  width: 100%;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  will-change: position, top, transform;
  z-index: 3;
}

.is-fixed {
  bottom: auto;
  top: auto;
}

.is-absolute {
  position: absolute;
  bottom: auto;
  top: auto;
}
</style>
